// src/components/Login.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { Turnstile } from '@marsidev/react-turnstile';
import './Login.css';
import {
  FaSpinner, FaGoogle, FaMicrosoft,
  FaEnvelope, FaMobileAlt, FaBuilding, FaCog, FaWhatsapp,
} from 'react-icons/fa';

import logoBLB from '../logo_blb.png';
import MaintenancePage from './MaintenancePage';
import LoginSupportWidget from './LoginSupportWidget';

if (api?.defaults) api.defaults.withCredentials = true;

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS & CACHE HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const CUSTOMIZATION_CACHE_TTL = 5 * 60 * 1000;

const getCachedCustomization = (id) => {
  try {
    const raw = sessionStorage.getItem(`cust_id_${id}`);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CUSTOMIZATION_CACHE_TTL) {
      sessionStorage.removeItem(`cust_id_${id}`);
      return null;
    }
    return data;
  } catch { return null; }
};

const setCachedCustomization = (id, data) => {
  try {
    sessionStorage.setItem(`cust_id_${id}`, JSON.stringify({ data, ts: Date.now() }));
  } catch {}
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: converte URL relativa do backend para URL absoluta
// (mesmo padrão do SettingsModal — precisa do baseURL do axios)
// ─────────────────────────────────────────────────────────────────────────────
const formatLoginImageUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  if (/^(https?:|data:|blob:)/.test(url)) return url;
  const rawBase =
    process.env.REACT_APP_API_BASE_URL ||
    api.defaults?.baseURL             ||
    process.env.REACT_APP_API_URL     ||
    '';
  const base = rawBase
    ? rawBase.replace(/\/api\/?$/, '').replace(/\/$/, '')
    : window.location.origin;
  return `${base}/${url.replace(/^\//, '')}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// EYE ICON
// ─────────────────────────────────────────────────────────────────────────────
const EyeIcon = ({ visible }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {visible ? (
      <>
        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
        <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
        <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
        <line x1="2" x2="22" y1="2" y2="22" />
      </>
    )}
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAINTENANCE BANNER (modo 'message')
// ─────────────────────────────────────────────────────────────────────────────
const MaintenanceBanner = ({ endDate }) => {
  const fmtDate = endDate
    ? new Date(endDate).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null;
  return (
    <div className="maintenance-login-banner">
      <span className="maintenance-login-banner__icon">🔧</span>
      <div>
        <strong>Sistema em manutenção</strong>
        <p>
          Estamos realizando melhorias no sistema.
          {fmtDate && <> Previsão de retorno: <b>{fmtDate}</b>.</>}
        </p>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MODAIS AUXILIARES
// ─────────────────────────────────────────────────────────────────────────────
function TenantPreviewBanner({ empresa, onExit }) {
  return (
    <div className="preview-banner-fixed">
      <span>
        <span style={{ color: '#818cf8' }}>👁 Preview:</span>{' '}
        <b style={{ color: '#fff' }}>{empresa}</b>
        <span style={{ color: '#6366f1', marginLeft: 8 }}>— modo visualização admin</span>
      </span>
      <button onClick={onExit} className="btn-exit-preview">✕ Sair do preview</button>
    </div>
  );
}

function AdminContextModal({ onSelectRoot, onSelectTenant, loading, tenants, error }) {
  const [selected, setSelected] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);

  const handleRoot = async () => {
    setLocalLoading(true);
    try { await onSelectRoot(); } finally { setLocalLoading(false); }
  };

  const handleTenant = async () => {
    if (!selected) return;
    setLocalLoading(true);
    try { await onSelectTenant(selected); } finally { setLocalLoading(false); }
  };

  const isLoading = loading || localLoading;

  return (
    <div className="admin-ctx-overlay">
      <div className="admin-ctx-content">
        <h2>Como deseja continuar?</h2>
        <p>Você entrou como <strong>Administrador Geral</strong>. Escolha o contexto de trabalho.</p>

        <button onClick={handleRoot} disabled={isLoading} className="btn-ctx-root">
          <FaCog size={20} color="#00e5bf" />
          <div className="btn-ctx-text">
            <div className="title">Visão Global (Dashboard Admin)</div>
            <div className="subtitle">Painel gerencial com os KPIs globais de todos os clientes.</div>
          </div>
        </button>

        <div className="ctx-divider"><hr /><span>ou acesse um tenant</span><hr /></div>

        {error && <div className="ctx-error">{error}</div>}

        {loading && !tenants.length ? (
          <div className="ctx-loading"><FaSpinner className="spinner" /> Carregando tenants...</div>
        ) : (
          <>
            <div className="ctx-tenant-list">
              {tenants.length === 0 ? (
                <div className="ctx-empty">Nenhum tenant ativo encontrado.</div>
              ) : (
                tenants.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setSelected(t.id)}
                    className={`btn-ctx-tenant ${selected === t.id ? 'selected' : ''}`}
                  >
                    {t.logo_url ? <img src={t.logo_url} alt={t.nome_empresa} /> : <FaBuilding size={18} />}
                    <span>{t.nome_empresa}</span>
                    {selected === t.id && <span className="check-mark">✓ selecionado</span>}
                  </button>
                ))
              )}
            </div>
            <button onClick={handleTenant} disabled={!selected || isLoading} className="btn-ctx-submit">
              {isLoading && selected ? <FaSpinner className="spinner" /> : <FaBuilding />} Acessar Tenant Selecionado
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const RECOVERY_METHODS = [
  { key: 'email',    label: 'E-mail',   Icon: FaEnvelope },
  { key: 'sms',      label: 'SMS',      Icon: FaMobileAlt },
  { key: 'whatsapp', label: 'WhatsApp', Icon: FaWhatsapp },
];

const RecoveryModal = ({ currentStage, handlers, formData, messages, loading, timer }) => {
  const {
    handleFormChange, handleMethodChange, handleRequestCodeSubmit,
    handleVerifyCodeSubmit, handleResetPasswordSubmit, handleBackToLogin, onTurnstileVerify,
  } = handlers;

  const { method, email, telefone, cpf, token, newPassword, confirmPassword } = formData;
  const { error, success } = messages;
  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
  const canalLabel = method === 'email' ? 'e-mail' : method === 'whatsapp' ? 'WhatsApp' : 'SMS';

  return (
    <div className={`recovery-modal-overlay ${currentStage.startsWith('forgot-') ? 'open' : ''}`}>
      <div className="recovery-modal-content">
        <button onClick={handleBackToLogin} className="modal-close-button">&times;</button>

        {currentStage === 'forgot-request-code' && (
          <form onSubmit={handleRequestCodeSubmit}>
            <h3>Recuperar Senha</h3>
            <p className="info-text">Escolha como deseja receber seu código de verificação.</p>
            <div className="recovery-method-group">
              {RECOVERY_METHODS.map(({ key, label, Icon }) => (
                <button key={key} type="button" onClick={() => handleMethodChange(key)}
                  className={`method-btn ${method === key ? 'active' : ''} ${key === 'whatsapp' ? 'method-btn--whatsapp' : ''}`}>
                  <Icon /> {label}
                </button>
              ))}
            </div>
            {method === 'email' ? (
              <div className="form-group">
                <label>E-mail Cadastrado:</label>
                <input type="email" name="email" value={email} onChange={handleFormChange} placeholder="seuemail@exemplo.com" required />
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>Telefone Celular:</label>
                  <input type="tel" name="telefone" value={telefone} onChange={handleFormChange} placeholder="(99) 99999-9999" required />
                </div>
                <div className="form-group">
                  <label>CPF:</label>
                  <input type="text" name="cpf" value={cpf} onChange={handleFormChange} placeholder="000.000.000-00" required />
                </div>
              </>
            )}
            {error && <p className="error-message">{error}</p>}
            <div className="form-group" style={{ display: 'flex', justifyContent: 'center', margin: '1rem 0' }}>
              <Turnstile siteKey={process.env.REACT_APP_CLOUDFLARE_SITE_KEY || '1x00000000000000000000AA'} onSuccess={onTurnstileVerify} />
            </div>
            <button type="submit" className="login-button blb-btn" disabled={loading}>
              {loading ? <FaSpinner className="spinner" /> : 'Validar e Enviar Código'}
            </button>
          </form>
        )}

        {currentStage === 'forgot-enter-token' && (
          <form onSubmit={handleVerifyCodeSubmit}>
            <h3>Verificar Código</h3>
            <p className="info-text">Digite o código de 6 dígitos enviado por {canalLabel}.</p>
            <div className="form-group">
              <label>Código de 6 dígitos:</label>
              <input type="text" name="token" value={token} onChange={handleFormChange} maxLength="6" required />
            </div>
            {error && <p className="error-message">{error}</p>}
            <button type="submit" className="login-button blb-btn" disabled={loading}>
              {loading ? <FaSpinner className="spinner" /> : 'Verificar'}
            </button>
            <div className="resend-timer">
              {timer > 0 ? (
                <span>Novo código em {formatTime(timer)}</span>
              ) : (
                <button type="button" className="forgot-password-button" onClick={handleRequestCodeSubmit}>Reenviar Código</button>
              )}
            </div>
          </form>
        )}

        {currentStage === 'forgot-set-new-password' && (
          <form onSubmit={handleResetPasswordSubmit}>
            <h3>Crie sua Nova Senha</h3>
            <div className="password-policy">
              <ul>
                <li>Pelo menos 12 caracteres</li>
                <li>Uma letra maiúscula</li>
                <li>Uma letra minúscula</li>
                <li>Um número</li>
                <li>Um símbolo (@$!%*?&)</li>
              </ul>
            </div>
            <div className="form-group">
              <label>Nova Senha:</label>
              <input type="password" name="newPassword" value={newPassword} onChange={handleFormChange} required />
            </div>
            <div className="form-group">
              <label>Confirme a Nova Senha:</label>
              <input type="password" name="confirmPassword" value={confirmPassword} onChange={handleFormChange} required />
            </div>
            {error && <p className="error-message">{error}</p>}
            <button type="submit" className="login-button blb-btn" disabled={loading}>
              {loading ? <FaSpinner className="spinner" /> : 'Salvar Nova Senha'}
            </button>
          </form>
        )}

        {currentStage === 'forgot-success' && (
          <div className="final-message-box">
            <h3>Sucesso!</h3>
            <p className="success-message">{success}</p>
            <button type="button" className="login-button blb-btn" onClick={handleBackToLogin}>Voltar para o Login</button>
          </div>
        )}
      </div>
    </div>
  );
};

const MfaModal = ({ visible, state, handlers, loading }) => {
  const { code, error, remember, method } = state;
  const { onCloseToLogin, onVerifyTotp, onSendSms, onVerifySms, onChangeCode, onToggleRemember } = handlers;

  return (
    <div className={`recovery-modal-overlay ${visible ? 'open' : ''}`}>
      <div className="recovery-modal-content">
        <button onClick={onCloseToLogin} className="modal-close-button">&times;</button>
        <h3>Autenticação de Dois Fatores</h3>

        {method === 'totp' && (
          <>
            <p className="info-text">Abra seu app autenticador e digite o código de 6 dígitos.</p>
            <div className="form-group">
              <label>Código do app (6 dígitos):</label>
              <input type="text" value={code} onChange={(e) => onChangeCode(e.target.value)} maxLength={6} inputMode="numeric" required />
            </div>
            <div className="form-group">
              <label className="checkbox">
                <input type="checkbox" checked={remember} onChange={(e) => onToggleRemember(e.target.checked)} />
                <span>Lembrar este dispositivo por 7 dias</span>
              </label>
            </div>
            {error && <div className="alert-error-container"><span>{error}</span></div>}
            <button type="button" className="login-button blb-btn" disabled={loading} onClick={onVerifyTotp}>
              {loading ? <FaSpinner className="spinner" /> : 'Verificar'}
            </button>
            <div className="mfa-alt">
              <button type="button" className="forgot-password-button" onClick={onSendSms}>
                Não consegue autenticar? Tente por SMS
              </button>
            </div>
          </>
        )}

        {method === 'sms' && (
          <>
            <p className="info-text">Enviamos um SMS com um código de 6 dígitos.</p>
            <div className="form-group">
              <label>Código SMS:</label>
              <input type="text" value={code} onChange={(e) => onChangeCode(e.target.value)} maxLength={6} inputMode="numeric" required />
            </div>
            <div className="form-group">
              <label className="checkbox">
                <input type="checkbox" checked={remember} onChange={(e) => onToggleRemember(e.target.checked)} />
                <span>Lembrar este dispositivo por 7 dias</span>
              </label>
            </div>
            {error && <div className="alert-error-container"><span>{error}</span></div>}
            <button type="button" className="login-button blb-btn" disabled={loading} onClick={onVerifySms}>
              {loading ? <FaSpinner className="spinner" /> : 'Verificar SMS'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

function EmpresaSelector({ empresas, loading, selectedId, onChange }) {
  return (
    <div className="form-group">
      <label htmlFor="empresa_id">ÁREA DE ACESSO (EMPRESA):</label>
      {loading ? (
        <div className="empresa-loading"><FaSpinner className="spinner" /> Carregando...</div>
      ) : (
        <div className="input-prefix-wrapper">
          <span className="input-prefix-icon"><FaBuilding size={14} /></span>
          <select
            id="empresa_id"
            value={selectedId || ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
            required
          >
            <option value="">— Selecione sua empresa —</option>
            {empresas.map((e) => (
              <option key={e.id} value={e.id}>{e.nome_empresa}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
export default function Login({ onLoginSuccess }) {
  const [loginData, setLoginData]       = useState({ email: '', senha: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [cfToken, setCfToken]           = useState(null);
  const location  = useLocation();
  const navigate  = useNavigate();
  const exchangeProcessed = useRef(false);

  const [empresas, setEmpresas]                   = useState([]);
  const [empresasLoading, setEmpresasLoading]     = useState(true);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);

  // ── MANUTENÇÃO ────────────────────────────────────────────────────────────
  const [maintenanceInfo, setMaintenanceInfo] = useState({
    isActive: false, endDate: null, displayMode: 'message',
  });
  const [showMaintenancePage, setShowMaintenancePage] = useState(false);

  // ── NOVIDADE: customização visual do tenant na tela de login ──────────────
  // logoUrl       → exibido no painel esquerdo ao lado do logo BLB
  // backgroundUrl → substitui o carrossel no painel direito
  const [loginCustomConfig, setLoginCustomConfig] = useState({
    logoUrl:       null,
    backgroundUrl: null,
  });
  // true quando a imagem de fundo da empresa terminou de carregar
  const [customBgLoaded, setCustomBgLoaded] = useState(false);

  // ── PREVIEW / ADMIN ───────────────────────────────────────────────────────
  const [previewBanner, setPreviewBanner] = useState({ active: false, empresa: null });
  const exitPreviewBanner = useCallback(() => setPreviewBanner({ active: false, empresa: null }), []);

  const [adminCtx, setAdminCtx] = useState({
    open: false, rawToken: null, tenants: [], loading: false, error: '',
  });

  // ── RECOVERY ──────────────────────────────────────────────────────────────
  const [recoveryData, setRecoveryData] = useState({
    method: 'email', email: '', telefone: '', cpf: '',
    token: '', newPassword: '', confirmPassword: '',
  });
  const [messages, setMessages]     = useState({ error: '', success: '' });
  const [loading, setLoading]       = useState(false);
  const [currentStage, setCurrentStage] = useState('login');
  const [timer, setTimer]           = useState(180);
  const [resetToken, setResetToken] = useState(null);

  // ── MFA ───────────────────────────────────────────────────────────────────
  const [mfa, setMfa]           = useState({ ticket: null, code: '', method: 'totp', remember: true });
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaSetup, setMfaSetup] = useState({
    open: false, qrDataUrl: '', secret: '', otpauth: '',
    code: '', busy: false, ticket: null, email: '',
  });
  const [ssoSetupCtx, setSsoSetupCtx] = useState({ provider: null, email: null, ticket: null });

  // ── CARROSSEL ─────────────────────────────────────────────────────────────
  const truckImages = [
    'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?q=80&w=2070&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1519003722811-9ca46b3f7fdb?q=80&w=2070&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1586191582056-6218fcfdd8f9?q=80&w=2070&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1506469717960-433cebe3f181?q=80&w=2070&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1616432043562-3671ea2e5242?q=80&w=2070&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1592838064575-70ed626d3a0e?q=80&w=2070&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=2070&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?q=80&w=2070&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1563720360172-67b8f3dce741?q=80&w=2070&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1471958680802-1345a694ba6d?q=80&w=2070&auto=format&fit=crop',
    'https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=2070&auto=format&fit=crop', // supercar (candidata p/ Lamborghini)
    'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?q=80&w=2070&auto=format&fit=crop',     // esportivo amarelo
    'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?q=80&w=2070&auto=format&fit=crop',  // carro clássico/muscle
  ];

  const [bgIndex, setBgIndex] = useState(0);

  // Carrossel: pausa automaticamente quando o fundo da empresa está ativo
  useEffect(() => {
    if (customBgLoaded) return; // ← pausa o interval quando empresa tem bg
    const t = setInterval(() => setBgIndex((prev) => (prev + 1) % truckImages.length), 5000);
    return () => clearInterval(t);
  }, [truckImages.length, customBgLoaded]);

  // ── SSO ───────────────────────────────────────────────────────────────────
  const backendBase = useMemo(() => {
    const base = process.env.REACT_APP_API_BASE_URL || api.defaults?.baseURL || 'http://localhost:3001';
    return base.replace(/\/api\/?$/i, '');
  }, []);

  const SSO_ENDPOINTS = useMemo(() => ({
    google:    process.env.REACT_APP_SSO_GOOGLE_URL || `${backendBase}/api/auth/google`,
    microsoft: process.env.REACT_APP_SSO_MS_URL     || `${backendBase}/api/auth/microsoft`,
  }), [backendBase]);

  // ── LISTA DE EMPRESAS ─────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setEmpresasLoading(true);
      try {
        const { data } = await api.get('/api/auth/empresas');
        setEmpresas(data.empresas || []);
      } catch (err) {
        console.error('Erro ao carregar empresas', err);
      } finally {
        setEmpresasLoading(false);
      }
    })();
  }, []);

  const selectedEmpresaNome = useMemo(
    () => empresas.find((e) => e.id === selectedEmpresaId)?.nome_empresa || null,
    [empresas, selectedEmpresaId],
  );

  const clearUrlQuery = useCallback(() => {
    try { window.history.replaceState({}, document.title, window.location.pathname); } catch {}
  }, []);

  const decodePossiblyDoubleEncoded = useCallback((value) => {
    if (!value || typeof value !== 'string' || value.length > 4096) return value;
    let v = value;
    for (let i = 0; i < 2; i++) {
      try { const dv = decodeURIComponent(v); if (dv === v) break; v = dv; } catch { break; }
    }
    return v;
  }, []);

  const resetTransientMessages = useCallback(() => setMessages({ error: '', success: '' }), []);

  const startSSO = useCallback((provider) => {
    if (!selectedEmpresaId) {
      setMessages({ error: 'Selecione sua empresa antes de continuar com SSO.', success: '' });
      return;
    }
    const baseUrl = `${window.location.origin}${window.location.pathname}`;
    window.location.href = `${SSO_ENDPOINTS[provider]}?redirect_uri=${encodeURIComponent(baseUrl)}&empresa_id=${selectedEmpresaId}`;
  }, [SSO_ENDPOINTS, selectedEmpresaId]);

  const handleGoogleSSO    = useCallback(() => startSSO('google'),    [startSSO]);
  const handleMicrosoftSSO = useCallback(() => startSSO('microsoft'), [startSSO]);

  // ── POST-LOGIN ────────────────────────────────────────────────────────────
  const handlePostLogin = useCallback(async (token, userData = null) => {
    // [FASE 3] Modo cookie: o backend já setou o cookie HttpOnly de sessão no
    // login/exchange. Limpamos qualquer credencial legada para o backend
    // (header-first) não usar um token velho; o /me abaixo autentica pelo cookie.
    localStorage.removeItem('token');
    delete api.defaults.headers.common.Authorization;
    let me = userData;

    if (!me || !me.perfil) {
      try {
        const { data } = await api.get('/api/usuarios/me');
        me = data;
      } catch (err) {
        setMessages({ error: err?.response?.data?.error || 'Falha ao carregar dados do usuário.', success: '' });
        localStorage.removeItem('token');
        delete api.defaults.headers.common.Authorization;
        return;
      }
    }

    if (me.perfil !== 'admin_geral') {
      if (selectedEmpresaId && Number(me?.cliente_id) !== Number(selectedEmpresaId)) {
        localStorage.removeItem('token');
        delete api.defaults.headers.common.Authorization;
        setMessages({ error: 'A empresa selecionada não corresponde ao cadastro deste usuário.', success: '' });
        setCurrentStage('login');
        return;
      }
      onLoginSuccess?.({ token, ...me });
      navigate('/dashboard');
      return;
    }

    setAdminCtx((prev) => ({ ...prev, open: true, rawToken: token, loading: true, error: '' }));
    try {
      const { data } = await api.get('/api/auth/available-tenants');
      setAdminCtx((prev) => ({ ...prev, loading: false, tenants: data?.tenants || [] }));
    } catch {
      setAdminCtx((prev) => ({ ...prev, loading: false, error: 'Lista de tenants indisponível.', tenants: [] }));
    }
  }, [navigate, onLoginSuccess, selectedEmpresaId]);

  const handleAdminSelectRoot = useCallback(async () => {
    setAdminCtx((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const { data } = await api.post('/api/auth/clear-tenant');
      if (!data?.token) throw new Error('Token não retornado.');
      // [FASE 3] /clear-tenant já reemitiu o cookie de sessão (sem active_tenant_id).
      // Não reinjetamos header; o /me abaixo usa o cookie.
      const { data: me } = await api.get('/api/usuarios/me');
      setAdminCtx({ open: false, rawToken: null, tenants: [], loading: false, error: '' });
      onLoginSuccess?.({ token: data.token, ...me });
      // Sem active_tenant_id, o DashboardRoute renderiza o DashboardAdmin (KPIs globais).
      navigate('/dashboard');
    } catch {
      setAdminCtx((prev) => ({ ...prev, loading: false, error: 'Falha ao limpar contexto.' }));
    }
  }, [navigate, onLoginSuccess]);

  const handleAdminSelectTenant = useCallback(async (clienteId) => {
    setAdminCtx((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const { data } = await api.post('/api/auth/switch-tenant', { cliente_id: clienteId });
      if (!data?.token) throw new Error('Token não retornado.');
      // [FASE 3] /switch-tenant já reemitiu o cookie de sessão com o active_tenant_id.
      // Não reinjetamos header; o /me abaixo usa o cookie.
      const { data: me } = await api.get('/api/usuarios/me');
      setAdminCtx({ open: false, rawToken: null, tenants: [], loading: false, error: '' });
      onLoginSuccess?.({ token: data.token, ...me });
      navigate('/dashboard');
    } catch {
      setAdminCtx((prev) => ({ ...prev, loading: false, error: 'Falha ao trocar de tenant.' }));
    }
  }, [navigate, onLoginSuccess]);

  // ── NOVIDADE: reset imediato do fundo ao trocar de empresa ────────────────
  useEffect(() => {
    setCustomBgLoaded(false);
    // Não limpa loginCustomConfig aqui — fetchCustomization fará isso
  }, [selectedEmpresaId]);

  // ── CUSTOMIZAÇÃO / MANUTENÇÃO ─────────────────────────────────────────────
  const fetchCustomization = useCallback(async () => {
    if (!selectedEmpresaId) {
      setMaintenanceInfo({ isActive: false, endDate: null, displayMode: 'message' });
      setLoginCustomConfig({ logoUrl: null, backgroundUrl: null });
      return;
    }

    const cached = getCachedCustomization(selectedEmpresaId);
    if (cached) {
      setMaintenanceInfo({
        isActive:    cached.maintenanceInfo?.isActive    ?? false,
        endDate:     cached.maintenanceInfo?.endDate     ?? null,
        displayMode: cached.maintenanceInfo?.displayMode ?? 'message',
      });
      // Restaura a config visual do cache também
      if (cached.loginCustomConfig) {
        setLoginCustomConfig(cached.loginCustomConfig);
      }
      return;
    }

    try {
      const { data } = await api.get(`/api/system-hub/public/customize/${selectedEmpresaId}`);

      const maintenanceData = {
        isActive:    data.maintenanceInfo?.isActive    ?? false,
        endDate:     data.maintenanceInfo?.endDate     ?? null,
        displayMode: data.maintenanceInfo?.displayMode ?? 'message',
      };
      setMaintenanceInfo(maintenanceData);

      // NOVIDADE: extrai e formata as URLs de imagem do login_config
      const lc = data.login_config || {};
      const customConfig = {
        logoUrl:       lc.logo_url       ? formatLoginImageUrl(lc.logo_url)       : null,
        backgroundUrl: lc.background_url ? formatLoginImageUrl(lc.background_url) : null,
      };
      setLoginCustomConfig(customConfig);

      // Salva no cache incluindo a config visual
      setCachedCustomization(selectedEmpresaId, {
        customization:     data,
        maintenanceInfo:   maintenanceData,
        loginCustomConfig: customConfig,
      });
    } catch {
      setMaintenanceInfo({ isActive: false, endDate: null, displayMode: 'message' });
      setLoginCustomConfig({ logoUrl: null, backgroundUrl: null });
    }
  }, [selectedEmpresaId]);

  const fetchMaintenanceStatus = useCallback(async () => {
    if (!selectedEmpresaId) return;
    try {
      const { data } = await api.get(`/api/system-hub/public/customize/${selectedEmpresaId}`);
      setMaintenanceInfo({
        isActive:    data.maintenanceInfo?.isActive    ?? false,
        endDate:     data.maintenanceInfo?.endDate     ?? null,
        displayMode: data.maintenanceInfo?.displayMode ?? 'message',
      });
    } catch {}
  }, [selectedEmpresaId]);

  useEffect(() => { fetchCustomization(); }, [fetchCustomization]);

  useEffect(() => {
    if (!selectedEmpresaId) return;
    const id = setInterval(fetchMaintenanceStatus, 30_000);
    return () => clearInterval(id);
  }, [selectedEmpresaId, fetchMaintenanceStatus]);

  // ── QUERY PARAMS ──────────────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const error = params.get('error');
    if (error) {
      setMessages({ error: decodePossiblyDoubleEncoded(params.get('msg') || 'Falha no SSO.'), success: '' });
      clearUrlQuery();
      return;
    }

    const returnEmpresaId = params.get('empresa_id');
    if (returnEmpresaId) setSelectedEmpresaId(Number(returnEmpresaId));

    const exchangeCode = params.get('code');
    if (exchangeCode && !params.get('mfa') && !params.get('mfa_setup')) {
      if (exchangeProcessed.current) return;
      exchangeProcessed.current = true;
      clearUrlQuery();
      (async () => {
        setLoading(true);
        setMessages({ error: '', success: '' });
        try {
          const { data } = await api.post('/api/auth/exchange', { code: exchangeCode });
          await handlePostLogin(data.token);
        } catch (err) {
          const errorMsg = err?.response?.data?.error || 'Erro ao conectar.';
          setMessages({ error: errorMsg, success: '' });
          if (err?.response?.status === 503 || errorMsg.toLowerCase().includes('manutenção') || errorMsg.toLowerCase().includes('manutencao')) {
            fetchMaintenanceStatus();
          }
        } finally { setLoading(false); }
      })();
      return;
    }

    const mfaFlag   = params.get('mfa');
    const ticketRaw = params.get('ticket');
    if (mfaFlag === '1' && ticketRaw) {
      clearUrlQuery();
      setMfa({ ticket: decodePossiblyDoubleEncoded(ticketRaw), code: '', method: 'totp', remember: true });
      setCurrentStage('mfa');
      return;
    }

    const mfaSetupFlag = params.get('mfa_setup');
    const ssoEmailRaw  = params.get('email');
    const ssoTicketRaw = params.get('ticket');
    if (mfaSetupFlag === '1' && ssoEmailRaw) {
      clearUrlQuery();
      const email  = decodePossiblyDoubleEncoded(ssoEmailRaw);
      const ticket = decodePossiblyDoubleEncoded(ssoTicketRaw || '');
      setSsoSetupCtx({ provider: params.get('provider'), email, ticket: ticket || null });
      setLoginData((prev) => ({ ...prev, email }));
      (async () => {
        setCurrentStage('mfa_setup');
        setMfaSetup((prev) => ({ ...prev, busy: true, open: true, code: '', ticket: ticket || null, email }));
        try {
          const payload = ticket ? { email, mfa_setup_ticket: ticket } : { email };
          const { data } = await api.post('/api/usuarios/mfa/setup/start', payload);
          setMfaSetup((prev) => ({ ...prev, busy: false, qrDataUrl: data.qrDataUrl, secret: data.secret, otpauth: data.otpauth, open: true, ticket: ticket || null, email }));
          setMessages({ error: '', success: 'Configure seu app autenticador.' });
        } catch (err) {
          setCurrentStage('login');
          setMessages({ error: err?.response?.data?.error || 'Falha ao iniciar configuração do MFA.', success: '' });
        }
      })();
      return;
    }

    const previewCode = params.get('admin_preview');
    if (previewCode) {
      clearUrlQuery();
      (async () => {
        try {
          const { data } = await api.post('/api/auth/exchange-preview', { code: previewCode });
          setPreviewBanner({ active: true, empresa: data.empresa });
          if (data.empresa_id) setSelectedEmpresaId(Number(data.empresa_id));
        } catch (err) { console.error('Erro no preview:', err); }
      })();
      return;
    }

    // [FASE 3] Removido o fluxo legado ?token= na URL (token cru em
    // histórico/logs/Referer é vetor de vazamento). O SSO moderno usa
    // ?code= + POST /api/auth/exchange, que entrega a sessão por cookie.
  }, [location.search, clearUrlQuery, handlePostLogin, decodePossiblyDoubleEncoded, fetchMaintenanceStatus]);

  // ── TIMER DE RECUPERAÇÃO ──────────────────────────────────────────────────
  useEffect(() => {
    if (currentStage !== 'forgot-enter-token' || timer <= 0) return;
    const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [currentStage, timer]);

  // ── LOGIN HANDLERS ────────────────────────────────────────────────────────
  const handleLoginChange = useCallback((e) => {
    const { id, value } = e.target;
    setLoginData((prev) => ({ ...prev, [id]: value }));
  }, []);

  const beginMfaSetupFromLogin = useCallback(async (setupTicket, emailParam) => {
    const emailToUse = (emailParam || loginData.email || '').trim();
    setMfaSetup((prev) => ({ ...prev, busy: true, open: true, code: '', ticket: setupTicket || prev.ticket || null, email: emailToUse }));
    try {
      const payload = { email: emailToUse, ...(setupTicket ? { mfa_setup_ticket: setupTicket } : {}) };
      const { data } = await api.post('/api/usuarios/mfa/setup/start', payload);
      setMfaSetup((prev) => ({ ...prev, busy: false, qrDataUrl: data.qrDataUrl, secret: data.secret, otpauth: data.otpauth, open: true, ticket: setupTicket || prev.ticket || null, email: emailToUse }));
    } catch (err) {
      setMfaSetup((prev) => ({ ...prev, busy: false, open: false }));
      throw err;
    }
  }, [loginData.email]);

  const continueAfterSetup = useCallback(async () => {
    try {
      resetTransientMessages();
      const { data } = await api.post('/api/usuarios/login', { email: loginData.email, senha: loginData.senha, empresa_id: selectedEmpresaId });
      if (data?.needsPasswordChange) { navigate(`/definir-nova-senha/${data.changePasswordTicket}`); return; }
      if (data?.mfa_setup_required)  { setCurrentStage('mfa_setup'); await beginMfaSetupFromLogin(data.mfa_setup_ticket, loginData.email); return; }
      if (data?.mfa_required)        { setMfa({ ticket: data.mfa_ticket, code: '', method: 'totp', remember: true }); setCurrentStage('mfa'); return; }
      await handlePostLogin(data.token, data);
    } catch (err) {
      setCurrentStage('login');
      setMessages({ error: err?.response?.data?.error || 'Falha ao continuar após configuração do MFA.', success: '' });
    }
  }, [loginData, selectedEmpresaId, navigate, handlePostLogin, beginMfaSetupFromLogin, resetTransientMessages]);

  const verifyMfaSetupFromLogin = useCallback(async () => {
    if (!mfaSetup.code || mfaSetup.code.trim().length < 6) { setMessages({ error: 'Digite os 6 dígitos.', success: '' }); return; }
    setMfaSetup((prev) => ({ ...prev, busy: true }));
    try {
      await api.post('/api/usuarios/mfa/setup/verify', { email: (mfaSetup.email || loginData.email || '').trim(), code: mfaSetup.code.trim(), ...(mfaSetup.ticket ? { mfa_setup_ticket: mfaSetup.ticket } : {}) });
      setMfaSetup((prev) => ({ ...prev, busy: false, open: false, code: '' }));
      if (ssoSetupCtx?.provider) { const p = ssoSetupCtx.provider; setSsoSetupCtx({ provider: null, email: null, ticket: null }); setCurrentStage('login'); startSSO(p); return; }
      await continueAfterSetup();
    } catch (err) {
      setMfaSetup((prev) => ({ ...prev, busy: false }));
      setMessages({ error: err?.response?.data?.error || 'Código inválido.', success: '' });
    }
  }, [mfaSetup, loginData.email, ssoSetupCtx, startSSO, continueAfterSetup]);

  const closeMfaSetupModal = useCallback(() => {
    setMfaSetup({ open: false, qrDataUrl: '', secret: '', otpauth: '', code: '', busy: false, ticket: null, email: '' });
    setSsoSetupCtx({ provider: null, email: null, ticket: null });
    setCurrentStage('login');
  }, []);

  const handleSubmitLogin = useCallback(async (e) => {
    e.preventDefault();
    if (!selectedEmpresaId) { setMessages({ error: 'Selecione sua empresa antes de continuar.', success: '' }); return; }
    if (!cfToken && process.env.REACT_APP_CLOUDFLARE_SITE_KEY) {
      setMessages({ error: 'Por favor, aguarde a verificação de segurança (Cloudflare).', success: '' });
      return;
    }
    setMessages({ error: '', success: '' });
    setLoading(true);
    try {
      const { data } = await api.post('/api/usuarios/login', { email: loginData.email, senha: loginData.senha, empresa_id: selectedEmpresaId, cfToken });
      if (data?.needsPasswordChange) { navigate(`/definir-nova-senha/${data.changePasswordTicket}`); return; }
      if (data?.mfa_setup_required) {
        setCurrentStage('mfa_setup');
        try { await beginMfaSetupFromLogin(data.mfa_setup_ticket, loginData.email); }
        catch (e2) { setCurrentStage('login'); setMessages({ error: e2?.response?.data?.error || 'Falha ao iniciar configuração do MFA.', success: '' }); }
        return;
      }
      if (data?.mfa_required) { setMfa({ ticket: data.mfa_ticket, code: '', method: 'totp', remember: true }); setCurrentStage('mfa'); return; }
      await handlePostLogin(data.token, data);
    } catch (err) {
      const errorMsg = err?.response?.data?.error || 'Erro ao conectar.';
      setMessages({ error: errorMsg, success: '' });
      if (err?.response?.status === 503 || errorMsg.toLowerCase().includes('manutenção') || errorMsg.toLowerCase().includes('manutencao')) {
        await fetchMaintenanceStatus();
        setShowMaintenancePage(true);
      }
    } finally { setLoading(false); }
  }, [loginData, selectedEmpresaId, navigate, beginMfaSetupFromLogin, handlePostLogin, fetchMaintenanceStatus, cfToken]);

  const handleFormChange           = useCallback((e) => { const { name, value } = e.target; setRecoveryData((prev) => ({ ...prev, [name]: value })); }, []);
  const handleMethodChange         = useCallback((method) => { setRecoveryData((prev) => ({ ...prev, method })); setMessages({ error: '', success: '' }); }, []);
  const handleBackToLogin          = useCallback(() => { setCurrentStage('login'); setMessages({ error: '', success: '' }); setRecoveryData({ method: 'email', email: '', telefone: '', cpf: '', token: '', newPassword: '', confirmPassword: '' }); setTimer(180); setResetToken(null); setSsoSetupCtx({ provider: null, email: null, ticket: null }); }, []);

  const handleRequestCodeSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!cfToken && process.env.REACT_APP_CLOUDFLARE_SITE_KEY) { setMessages({ error: 'Por favor, aguarde a verificação de segurança (Cloudflare).', success: '' }); return; }
    setMessages({ error: '', success: '' });
    setLoading(true);
    try {
      const payload = { method: recoveryData.method, cfToken };
      if (recoveryData.method === 'email') { payload.email = recoveryData.email; }
      else { payload.telefone = recoveryData.telefone; payload.cpf = recoveryData.cpf; }
      await api.post('/api/usuarios/reset-password/request', payload);
      setCurrentStage('forgot-enter-token');
      setTimer(180);
    } catch (err) { setMessages({ error: err?.response?.data?.error || 'Erro ao solicitar.', success: '' }); }
    finally { setLoading(false); }
  }, [recoveryData, cfToken]);

  const handleVerifyCodeSubmit = useCallback(async (e) => {
    e.preventDefault();
    setMessages({ error: '', success: '' });
    setLoading(true);
    try {
      const { data } = await api.post('/api/usuarios/reset-password/verify', { token: recoveryData.token, method: recoveryData.method, email: recoveryData.email, cpf: recoveryData.cpf });
      setResetToken(data.resetToken);
      setCurrentStage('forgot-set-new-password');
    } catch { setMessages({ error: 'Código inválido.', success: '' }); }
    finally { setLoading(false); }
  }, [recoveryData]);

  const handleResetPasswordSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (recoveryData.newPassword !== recoveryData.confirmPassword) { setMessages({ error: 'As senhas não coincidem.', success: '' }); return; }
    setMessages({ error: '', success: '' });
    setLoading(true);
    try {
      const { data } = await api.post('/api/usuarios/reset-password/set-new-password', { resetToken, newPassword: recoveryData.newPassword });
      setMessages({ success: data.message, error: '' });
      setCurrentStage('forgot-success');
    } catch (err) { setMessages({ error: err?.response?.data?.error || 'Erro ao salvar.', success: '' }); }
    finally { setLoading(false); }
  }, [recoveryData, resetToken]);

  const recoveryHandlers = useMemo(() => ({
    handleFormChange, handleMethodChange, handleRequestCodeSubmit,
    handleVerifyCodeSubmit, handleResetPasswordSubmit, handleBackToLogin,
    onTurnstileVerify: setCfToken,
  }), [handleFormChange, handleMethodChange, handleRequestCodeSubmit, handleVerifyCodeSubmit, handleResetPasswordSubmit, handleBackToLogin]);

  const handleMfaCloseToLogin   = useCallback(() => { setMfa({ ticket: null, code: '', method: 'totp', remember: true }); setMessages({ error: '', success: '' }); setCurrentStage('login'); }, []);
  const handleMfaChangeCode     = useCallback((v) => setMfa((prev) => ({ ...prev, code: v })), []);
  const handleMfaToggleRemember = useCallback((c) => setMfa((prev) => ({ ...prev, remember: c })), []);

  const handleMfaVerifyTotp = useCallback(async () => {
    if (!mfa.ticket || !mfa.code) return;
    if (!selectedEmpresaId) { setMessages({ error: 'Selecione sua empresa antes de validar o MFA.', success: '' }); return; }
    setMfaLoading(true);
    try {
      const { data } = await api.post('/api/usuarios/mfa/challenge/verify-totp', { mfa_ticket: mfa.ticket, code: mfa.code, remember: mfa.remember, empresa_id: selectedEmpresaId });
      await handlePostLogin(data.token, data);
    } catch (err) { setMessages({ error: err?.response?.data?.error || 'Código inválido.', success: '' }); }
    finally { setMfaLoading(false); }
  }, [mfa, selectedEmpresaId, handlePostLogin]);

  const handleMfaSendSms = useCallback(async () => {
    if (!mfa.ticket) return;
    setMfaLoading(true);
    try { await api.post('/api/usuarios/mfa/challenge/sms/send', { mfa_ticket: mfa.ticket }); setMfa((prev) => ({ ...prev, method: 'sms', code: '' })); setMessages({ error: '', success: 'Código SMS enviado com sucesso.' }); }
    catch (err) { setMessages({ error: err?.response?.data?.error || 'Falha ao enviar SMS.', success: '' }); }
    finally { setMfaLoading(false); }
  }, [mfa.ticket]);

  const handleMfaVerifySms = useCallback(async () => {
    if (!mfa.ticket || !mfa.code) return;
    if (!selectedEmpresaId) { setMessages({ error: 'Selecione sua empresa antes de validar o MFA por SMS.', success: '' }); return; }
    setMfaLoading(true);
    try {
      const { data } = await api.post('/api/usuarios/mfa/challenge/sms/verify', { mfa_ticket: mfa.ticket, code: mfa.code, remember: mfa.remember, empresa_id: selectedEmpresaId });
      await handlePostLogin(data.token, data);
    } catch (err) { setMessages({ error: err?.response?.data?.error || 'Código SMS inválido.', success: '' }); }
    finally { setMfaLoading(false); }
  }, [mfa, selectedEmpresaId, handlePostLogin]);

  const mfaHandlers = useMemo(() => ({
    onCloseToLogin:   handleMfaCloseToLogin,
    onVerifyTotp:     handleMfaVerifyTotp,
    onSendSms:        handleMfaSendSms,
    onVerifySms:      handleMfaVerifySms,
    onChangeCode:     handleMfaChangeCode,
    onToggleRemember: handleMfaToggleRemember,
  }), [handleMfaCloseToLogin, handleMfaVerifyTotp, handleMfaSendSms, handleMfaVerifySms, handleMfaChangeCode, handleMfaToggleRemember]);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  if (showMaintenancePage && maintenanceInfo.isActive && maintenanceInfo.displayMode === 'page') {
    return <MaintenancePage endDate={maintenanceInfo.endDate} nomeEmpresa={selectedEmpresaNome} />;
  }

  return (
    <>
      {previewBanner.active && (
        <TenantPreviewBanner empresa={previewBanner.empresa} onExit={exitPreviewBanner} />
      )}

      {adminCtx.open && (
        <AdminContextModal
          tenants={adminCtx.tenants}
          loading={adminCtx.loading}
          error={adminCtx.error}
          onSelectRoot={handleAdminSelectRoot}
          onSelectTenant={handleAdminSelectTenant}
        />
      )}

      <div className="login-split-layout">

        {/* ── PAINEL ESQUERDO ─────────────────────────────────────────── */}
        <div className="login-left-panel">
          <div className="login-box-inner">

            <div className="blb-branding">
              <div className="blb-logo-wrapper">
                <img src={logoBLB} alt="BLB System" className="login-logo-blb" />
              </div>

              {/* NOVIDADE: logo da empresa selecionada ao lado do logo BLB */}
              {loginCustomConfig.logoUrl && selectedEmpresaId && (
                <div className="login-tenant-entry">
                  <span className="login-tenant-entry__sep" aria-hidden="true">×</span>
                  <img
                    src={loginCustomConfig.logoUrl}
                    alt={selectedEmpresaNome || 'Empresa'}
                    className="login-tenant-entry__logo"
                    onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                  />
                </div>
              )}

              <h2 className="login-title">Acesso ao Sistema</h2>
              {/* NOVIDADE: subtítulo personalizado quando empresa está selecionada */}
              <p className="login-subtitle">
                {selectedEmpresaNome
                  ? `Bem-vindo à plataforma · ${selectedEmpresaNome}`
                  : 'Bem-vindo de volta | Insira suas credenciais.'
                }
              </p>
            </div>

            <form onSubmit={handleSubmitLogin} className="login-form-area">
              <EmpresaSelector
                empresas={empresas}
                loading={empresasLoading}
                selectedId={selectedEmpresaId}
                onChange={setSelectedEmpresaId}
              />

              <div className="form-group">
                <label htmlFor="email">E-MAIL PROFISSIONAL</label>
                <div className="input-prefix-wrapper">
                  <span className="input-prefix-icon"><FaEnvelope size={14} /></span>
                  <input type="email" id="email" value={loginData.email} onChange={handleLoginChange} placeholder="nome@empresa.com" required autoComplete="username" />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="senha">SENHA</label>
                <div className="password-input-wrapper">
                  <input type={showPassword ? 'text' : 'password'} id="senha" value={loginData.senha} onChange={handleLoginChange} placeholder="••••••••" required autoComplete="current-password" />
                  <button type="button" className="password-toggle-btn" onClick={() => setShowPassword((p) => !p)} tabIndex={-1} aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>
                    <EyeIcon visible={showPassword} />
                  </button>
                </div>
              </div>

              <div className="form-options">
                <button type="button" className="forgot-password-button" onClick={() => setCurrentStage('forgot-request-code')}>
                  Esqueceu a senha?
                </button>
              </div>

              {messages.error && currentStage === 'login' && (
                <div className="alert-error-container"><span>{messages.error}</span></div>
              )}

              {maintenanceInfo.isActive && maintenanceInfo.displayMode === 'message' && (
                <MaintenanceBanner endDate={maintenanceInfo.endDate} />
              )}

              <div className="form-group" style={{ display: 'flex', justifyContent: 'center', margin: '1.5rem 0 1rem' }}>
                <Turnstile
                  siteKey={process.env.REACT_APP_CLOUDFLARE_SITE_KEY || '1x00000000000000000000AA'}
                  onSuccess={setCfToken}
                />
              </div>

              <button type="submit" className="login-button blb-btn" disabled={loading || empresasLoading}>
                {loading && currentStage === 'login' ? <FaSpinner className="spinner" /> : 'Entrar na Plataforma'}
              </button>

              <div className="sso-divider"><span>Ou entre usando SSO</span></div>

              <div className="sso-buttons">
                <button type="button" className="sso-btn google" onClick={handleGoogleSSO} disabled={loading} title="Entrar com o Google Workspace">
                  <FaGoogle className="sso-icon" />
                </button>
                <button type="button" className="sso-btn microsoft" onClick={handleMicrosoftSSO} disabled={loading} title="Entrar com o Microsoft 365">
                  <FaMicrosoft className="sso-icon" />
                </button>
              </div>
            </form>
          </div>

          <div className="blb-footer">
            <span>&copy; {new Date().getFullYear()} BLB System. Todos os direitos reservados.</span>
          </div>
        </div>

        {/* ── PAINEL DIREITO ───────────────────────────────────────────── */}
        <div className="login-right-panel">

          {/* Carrossel padrão — sempre presente como camada base */}
          <div className="carousel-container">
            {truckImages.map((img, index) => (
              <div
                key={index}
                className={`carousel-slide ${index === bgIndex ? 'active' : ''}`}
                style={{ backgroundImage: `url(${img})` }}
              />
            ))}
          </div>

          {/* NOVIDADE: fundo customizado da empresa — sobrepõe o carrossel */}
          {loginCustomConfig.backgroundUrl && (
            <div className="login-company-bg-layer">
              <img
                /* key garante remount e re-animação ao trocar de empresa */
                key={loginCustomConfig.backgroundUrl}
                src={loginCustomConfig.backgroundUrl}
                alt=""
                className={`login-company-bg-img ${customBgLoaded ? 'login-company-bg-img--loaded' : ''}`}
                onLoad={() => setCustomBgLoaded(true)}
                onError={() => {
                  setCustomBgLoaded(false);
                  setLoginCustomConfig((prev) => ({ ...prev, backgroundUrl: null }));
                }}
              />
            </div>
          )}

          {/* Overlay diagonal — mantido acima do fundo */}
          <div className="diagonal-overlay" />

          {/* NOVIDADE: branding da empresa no canto do painel — aparece após o bg carregar */}
          <div className={`login-company-branding ${customBgLoaded ? 'login-company-branding--visible' : ''}`}>
            {loginCustomConfig.logoUrl && (
              <img
                src={loginCustomConfig.logoUrl}
                alt={selectedEmpresaNome || ''}
                className="login-company-branding__logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            {selectedEmpresaNome && (
              <span className="login-company-branding__name">{selectedEmpresaNome}</span>
            )}
          </div>
        </div>

        {/* ── MODAIS ──────────────────────────────────────────────────── */}
        <RecoveryModal
          currentStage={currentStage}
          handlers={recoveryHandlers}
          formData={recoveryData}
          messages={messages}
          loading={loading}
          timer={timer}
        />

        <MfaModal
          visible={currentStage === 'mfa'}
          state={{ code: mfa.code, error: messages.error, remember: mfa.remember, method: mfa.method }}
          handlers={mfaHandlers}
          loading={mfaLoading}
        />

        {mfaSetup.open && (
          <div className="recovery-modal-overlay open">
            <div className="recovery-modal-content mfa-setup-card">
              <button onClick={closeMfaSetupModal} className="modal-close-button">&times;</button>
              <h3>Habilitar Autenticador (TOTP)</h3>
              <p className="info-text">Escaneie o QR Code abaixo com seu aplicativo de autenticação (Google Authenticator, Authy, etc).</p>
              <div className="mfa-setup-grid">
                <div className="qr-container">
                  {mfaSetup.qrDataUrl ? <img src={mfaSetup.qrDataUrl} alt="QR Code" /> : <div className="blb-loader">Gerando QR...</div>}
                </div>
                <div className="mfa-form-inputs">
                  <div className="form-group">
                    <label>Código do App (6 dígitos)</label>
                    <input type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={mfaSetup.code} onChange={(e) => setMfaSetup((prev) => ({ ...prev, code: e.target.value }))} />
                  </div>
                  {messages.error && <div className="alert-error-container"><span>{messages.error}</span></div>}
                </div>
              </div>
              <div className="modal-actions-right">
                <button type="button" className="btn-ghost" onClick={closeMfaSetupModal} disabled={mfaSetup.busy}>Cancelar</button>
                <button type="button" className="login-button blb-btn" onClick={verifyMfaSetupFromLogin} disabled={mfaSetup.busy || !mfaSetup.code}>
                  {mfaSetup.busy ? <FaSpinner className="spinner" /> : 'Validar Código'}
                </button>
              </div>
            </div>
          </div>
        )}

        <LoginSupportWidget empresaId={selectedEmpresaId} empresaNome={selectedEmpresaNome} />
      </div>
    </>
  );
}